import React, { useState, useEffect } from 'react';
import { BackButton, Input, Select, LengthOfStay, ErrorAlert, Button, ErrorMessage,PricingAsOfCYQuarter } from '../Components/Simple';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import { ApiError, MultiInput, MultiInputType, ProviderMapper } from '../Components/Combined';
import { poaValues, genderValues, serviceModeValues } from '../Constants/dropdownValues';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { IPPS as modelType } from '../Constants/modelTypes';
import useFiscalYear from '../Hooks/useFiscalYear';
import useFYPricingAsOfQuarter from '../Hooks/useFYPricingAsOfQuarter';
import useDrgGrouperApi from '../Hooks/useDrgGrouperApi';
import { useNavigate, useLocation } from 'react-router-dom';
import useSessionState from '../Hooks/useSessionState';
import useCollection from '../Hooks/useCollection';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { formatDateStringIfMatches, getDefaultDateString } from '../Utils/stringUtils';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { mapToSetters } from '../Utils/mapping';
import { enableAsyncPricingApi } from '../Utils/features';
import usePricerApi from '../Hooks/usePricerApi';
import { EXECUTE } from '../Constants/serviceMode';

export default function IPAcute() {
  const location = useLocation();
  const { prePopulatedMedicareId, prePopulatedNPI } = location.state || {};
  const [providerName, setProviderName] = useSessionState('', 'providerName');
  const [medicareId, setMedicareId] = useSessionState(prePopulatedMedicareId ?? '', 'medicareId');
  const [npi, setNpi] = useSessionState(prePopulatedNPI ?? '', 'npi');
  const [drgCode, setDRGCode] = useSessionState('', 'drg');
  const [drgGrouperDesc, setDRGGrouperDesc] = useSessionState('', 'description');
  const [drgGrouperVersion, setDRGGrouperVersion] = useSessionState('', 'grouperVersion');
  const [drgCodeAutofilled, setDRGCodeAutofilled] = useState(false);
  const [drgSuccess, setDRGSuccess] = useState(false);
  const [dischargeStatus, setDischargeStatus] = useSessionState('', 'dischargeStatus');
  const [billedAmountTotal, setBilledAmountTotal] = useSessionState('', 'billedAmountTotal');
  const [billedAmountOrgAcquistion, setBilledAmountOrgAcquistion] = useSessionState('', 'billedAmountOrgAcq');
  const [admitDate, setAdmitDate] = useSessionState(getDefaultDateString(), 'admitDate');
  const [dischargeDate, setDischargeDate] = useSessionState(getDefaultDateString(), 'dischargeDate');
  const [pricingFYQuarter, setPricingFYQuarterInputDate] = useFYPricingAsOfQuarter(null);
  const [birthDate, setBirthDate] = useSessionState('', 'birthDate');
  const [gender, setGender] = useSessionState('', 'gender');
  const [claimId, setClaimId] = useSessionState('', 'claimId');
  const [fiscalYear, setFiscalYearInputDate] = useFiscalYear(null);
  const [conditionCode, setConditionCode] = useSessionState('', 'conditionCode');
  const [ndc, setNDC] = useSessionState('', 'ndc');
  const [drgServiceMode, setDrgServiceMode] = useSessionState(EXECUTE, 'drgServiceMode');
  const [pricingDataDate, setPricingDataDate] = useSessionState('', 'pricingDataDate');
  const MINIMUM_AS_OF_FISCAL_YEAR = 2022;
  const navigate = useNavigate();

  const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);
  // const warmupPricer = useAsyncPricerApi(modelType, true);

  const drg = useDrgGrouperApi();

  const getNewProcedureCode = () => {
    return { procedureCode: '' };
  };

  const getNewDisanosisCode = () => {
    return {
      diagnosisCode: '',
      poa: ''
    };
  };

  const DEFAULT_PROCEDURE_CODES = [getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode()];
  const DEFAULT_DIAGNOSIS_CODES = [getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode()];

  const proceduresCollection = useCollection(DEFAULT_PROCEDURE_CODES, getNewProcedureCode, 'procedureCodes');
  const diagnosisesCollection = useCollection(DEFAULT_DIAGNOSIS_CODES, getNewDisanosisCode, 'diagnosisCodes');

  const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
    claimId,
    providerName,
    medicareId,
    npi,
    drgCode,
    dischargeStatus,
    birthDate,
    gender,
    admitDate,
    dischargeDate,
    pricingDataDate,
    billedAmountTotal,
    billedAmountOrgAcquisition: billedAmountOrgAcquistion,
    conditionCode,
    ndc,
    drgServiceMode,
    diagnosisCodes: diagnosisesCollection.values,
    procedureCodes: proceduresCollection.values
  });

  const handleFileSave = () => {
    resetApis();
    fileExportExecute();
  };

  const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

  const handleImportChange = (e) => {
    resetApis();
    fileImportOnChange(e);
  };

  const handleSubmit = async () => {
    drg.reset(); // don't want to be showing error messages
    fileExportReset();
    fileImportReset();

    if (enableAsyncPricingApi) {
      await pricer.execute(formatRequest());
    } else {
      await pricer.execute(fiscalYear, formatRequest());
    }
  };

  const formatRequest = () => {
    const request = {
      claimID: claimId || null,
      medicareID: medicareId || null,
      paymentMethodology: "FFS",
      dob: formatDateStringIfMatches(birthDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
      gender: gender || null,
      fromDate: formatDateStringIfMatches(admitDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
      toDate: formatDateStringIfMatches(dischargeDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
      pricingAsOfDate: pricingFYQuarter || null,
      dischargeStatus: dischargeStatus || null,
      drg: drgCode || null,
      ndc: ndc || null,
      conditionCode: conditionCode || null,
      drgServiceMode,
      diagnosis: [
        ...diagnosisesCollection.values.filter(dc => dc.diagnosisCode).map(dc => {
          return {
            icdDiag: dc.diagnosisCode.toUpperCase() || null,
            poa: dc.poa || null
          }
        })
      ],
      icdProc: [
        ...proceduresCollection.values.filter(pc => pc.procedureCode).map(pc => pc.procedureCode.toUpperCase())
      ],
      serviceLineItems: []
    };

    const parsedBilledAmountTotal = !isNaN(billedAmountTotal) ? Number(billedAmountTotal) : 0;
    const parsedBilledAmountOrgAcquistion = !isNaN(billedAmountOrgAcquistion) ? Number(billedAmountOrgAcquistion) : 0;

    request.serviceLineItems.push({
      lineNumber: 1,
      revCode: '0121',
      billed: parsedBilledAmountTotal - parsedBilledAmountOrgAcquistion
    });

    if (parsedBilledAmountOrgAcquistion !== 0) {
      request.serviceLineItems.push({
        lineNumber: request.serviceLineItems.length + 1,
        revCode: '0810',
        billed: parsedBilledAmountOrgAcquistion
      });
    }

    return request;
  }

  const resetApis = () => {
    pricer.reset();
    drg.reset();
  };

  const handleReset = () => {
    setProviderName('');
    setMedicareId('');
    setNpi('');
    setDRGCode('');
    setDRGGrouperDesc('');
    setDRGGrouperVersion('');
    setDRGSuccess('');
    setBirthDate('');
    setGender('');
    setDischargeStatus('');
    setBilledAmountTotal('');
    setBilledAmountOrgAcquistion('');
    setClaimId('');
    setAdmitDate(getDefaultDateString());
    setDischargeDate(getDefaultDateString());
    setPricingDataDate(getDefaultDateString());
    proceduresCollection.setValues(DEFAULT_PROCEDURE_CODES);
    diagnosisesCollection.setValues(DEFAULT_DIAGNOSIS_CODES);
    setConditionCode('');
    setNDC('');
    setDrgServiceMode(EXECUTE);
    resetApis();
    fileExportReset();
    fileImportReset();
  };

  useEffect(() => {
    if (fileImportSuccess) {
      const setters = {
        claimId: setClaimId,
        providerName: setProviderName,
        medicareId: setMedicareId,
        npi: setNpi,
        drgCode: setDRGCode,
        dischargeStatus: setDischargeStatus,
        birthDate: setBirthDate,
        gender: setGender,
        admitDate: setAdmitDate,
        dischargeDate: setDischargeDate,
        pricingDataDate: setPricingDataDate,
        billedAmountTotal: setBilledAmountTotal,
        billedAmountOrgAcquisition: setBilledAmountOrgAcquistion,
        conditionCode: setConditionCode,
        ndc: setNDC,
        diagnosisCodes: diagnosisesCollection.setValues,
        procedureCodes: proceduresCollection.setValues,
        drgServiceMode: setDrgServiceMode
      };
      handleReset();
      mapToSetters(fileImportResult, setters);
    }
  }, [fileImportSuccess]);

  useEffect(() => {
    if (drg.success && drg.result) {
      setDRGCode((drg.result.finalDRG && drg.result.finalDRG.code && drg.result.finalDRG.code.toString()) || '');
      setDRGGrouperDesc((drg.result.finalDRG.description));
      setDRGGrouperVersion((drg.result.grouperVersion));
      setDRGSuccess((drg.success));
      setDRGCodeAutofilled(true);
      drg.reset();
      setTimeout(() => {
        setDRGCodeAutofilled(false);
      }, 3000);
    }
  }, [drg.success]);

  const handleRunDrgGrouper = () => {
    pricer.reset();
    fileImportReset();
    fileExportReset();
    drg.execute({
      birthDate,
      gender,
      admitDate,
      dischargeDate,
      dischargeStatus,
      diagnosisCodes: diagnosisesCollection.values,
      procedureCodes: proceduresCollection.values
    });
  };

  useEffect(() => {
    setFiscalYearInputDate(dischargeDate);
    if(fiscalYear < MINIMUM_AS_OF_FISCAL_YEAR)
      {
        setPricingDataDate(getDefaultDateString());
      }
  }, [dischargeDate]);

  useEffect(() => {
    setPricingFYQuarterInputDate(pricingDataDate);
  }, [pricingDataDate]);

  useEffect(() => {
    if (pricer.success) {
      navigate('result', {
        state: {
          fiscalYear,
          result: pricer.result,
          totalBilledOrgAcquistion: billedAmountOrgAcquistion,
          drgServiceMode,
          pricingFYQuarter
        }
      });
    }
  }, [pricer.success]);

  return (
    <div className="py-2">
      <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
        <div className="col-auto">
          <h3>IP Acute Pricer</h3>
          <div className="fs-6">
            Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
          </div>
        </div>
        <div className="col-auto">
          <BackButton />
        </div>
      </div>
      {(pricer.error || drg.error || fileImportError || fileExportError) && (<div>
        <ApiError error={pricer.error} />
        <ApiError error={drg.error} />
        {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
        {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
      </div>)}
      {(drgSuccess) && (<div className="alert alert-success" role="alert">
        DRG Grouper Message: {drgGrouperDesc ?? null} <br />
        DRG Grouper Version Applied: {drgGrouperVersion ?? null}
      </div>)}
      <form noValidate={true}>
        <div className="row my-2">
          <div className="col-md-3">
            <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
          </div>
          <ProviderMapper inputs={[
            {
              name: "providerName",
              label: "Provider Name",
              superscript: "1",
              value: providerName,
              onChange: setProviderName
            },
            {
              name: "medicareId",
              label: "Medicare ID",
              superscript: "1",
              value: medicareId,
              onChange: setMedicareId
            },
            {
              name: "npi",
              label: "NPI",
              sublabel: "(facilities may have multiple NPIs)",
              superscript: "1",
              value: npi,
              onChange: setNpi
            }
          ]} model={modelType} />
        </div>
        <div className="row my-2">
          <div className="col-md-3">
            <Input label="MS-DRG" id="ms-drg" labelClassName="fw-bold" onChange={setDRGCode} value={drgCode} placeholder="000" maxLength={3} className={drgCodeAutofilled ? 'autofilled' : ''} />
          </div>
          {enableAsyncPricingApi && <div className="col-md-3">
            <Select id="drg-service-mode" value={drgServiceMode} labelClassName="fw-bold" onChange={setDrgServiceMode} label="Set DRG Grouper Execution">
              {serviceModeValues.map((v, i) => <option key={i} value={v.value}>{v.text}</option>)}
            </Select>
          </div>}
          <div className="col-md-3">
            <Input label="Discharge Status" id="discharge-status" labelClassName="fw-bold" onChange={setDischargeStatus} value={dischargeStatus} placeholder="00" maxLength={2} />
          </div>
          <div className="col-md-3">
            <Input label="Date of Birth" preservePlaceholder={true} id="date-of-birth" labelClassName="fw-bold" type="date" onChange={setBirthDate} value={birthDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
          </div>
          <div className="col-md-3">
            <Select label="Gender" id="gender" labelClassName="fw-bold" onChange={setGender} value={gender}>
              {genderValues.map(gv => <option key={gv.value} value={gv.value}>{gv.text}</option>)}
            </Select>
          </div>
        </div>
        <div className="row my-2">
          <div className="col-md-3">
            <Input label="Admit Date" preservePlaceholder={true} id="admit-date" labelClassName="fw-bold" type="date" onChange={setAdmitDate} value={admitDate} />
          </div>
          <div className="col-md-3">
            <Input label="Discharge Date" preservePlaceholder={true} id="discharge-date" labelClassName="fw-bold" type="date" onChange={setDischargeDate} value={dischargeDate} />
          </div>
          <div className="col-md-3 align-self-center mt-4">
            <LengthOfStay fromDate={admitDate} toDate={dischargeDate} />
          </div>
          <div className="col-md-3 align-self-center mt-4 fw-bold">
            {fiscalYear !== null && <>FY{fiscalYear}</>}
          </div>
          {fiscalYear >= MINIMUM_AS_OF_FISCAL_YEAR && <>
          <div className="col-md-3">
            <Input label="Pricing Data As-of Date" superscript="2" preservePlaceholder={true} id="pricing-date" labelClassName="fw-bold" type="date" onChange={setPricingDataDate} value={pricingDataDate} />
          </div>
          <div className="col-md-3 align-self-center mt-4">
            <PricingAsOfCYQuarter pricingDataDate={pricingDataDate} />
          </div>
          </>}
        </div>
        <div className="row my-2">
          <div className="col-md-3">
            <Input label="Billed Amount: Total Incl Organ Acq" labelClassName="fw-bold" id="billed-amount-total" onChange={setBilledAmountTotal} value={billedAmountTotal} placeholder="0000.00" />
          </div>
          <div className="col-md-3">
            <Input label="Billed Amount: Organ Acquisition" labelClassName="fw-bold" id="billed-amount-organ-acquistion" onChange={setBilledAmountOrgAcquistion} value={billedAmountOrgAcquistion} placeholder="0000.00" />
          </div>
          <div className="col-md-3">
            <Input label="Condition Code" id="condition-code" labelClassName="fw-bold" onChange={setConditionCode} value={conditionCode} />
          </div>
          <div className="col-md-3">
            <Select label="NDC" id="ndc" value={ndc} labelClassName="fw-bold" onChange={setNDC}>
              <option value=""></option>
              <option value="00069108506">00069108506</option>
              <option value="00069108530">00069108530</option>
              <option value="00006505506">00006505506</option>
              <option value="00006505507">00006505507</option>
              <option value="NA">None of these NDCs</option>
            </Select>
          </div>
        </div>
        <div> <sup> 1 </sup> Pricing depends only on Medicare ID </div>
        {fiscalYear >= MINIMUM_AS_OF_FISCAL_YEAR && <div> <sup> 2 </sup> Only change this setting to price using historical (outdated) CMS provider data, removing retroactive data updates. </div>}
        <hr />
        <div className="row g-0">
          <div className="col-md-8 my-2">
            <MultiInput label="ICD-10 Diagnosis Codes"
              note="Enter ICD codes without the decimal."
              subLabel="Diagnosis"
              collection={diagnosisesCollection}
              lineCountLabel="Total Diagnosis Codes"
              id="diagnosis-codes"
              max={30}>
              <MultiInputType label="Diagnosis Code"
                name="diagnosisCode"
                type="text"
                maxLength={7} />
              <MultiInputType label="POA"
                name="poa"
                type="select"
                options={poaValues} />
            </MultiInput>
          </div>
          <div className="col-md-4 my-2">
            <MultiInput label="ICD-10 Procedure Codes"
              note="Enter ICD codes without the decimal."
              collection={proceduresCollection}
              lineCountLabel="Total Procedure Codes"
              max={30}
              id="procedure-codes">
              <MultiInputType label="Procedure Code"
                name="procedureCode"
                type="text"
                maxLength={7} />
            </MultiInput>
          </div>
        </div>
        <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
          <div className="col-auto align-self-center">
            <div className="row g-0">
              <div className="col-auto ms-2">
                <Button className="btn-outline-secondary" id="save-request-button"
                  disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                  onClick={handleFileSave}
                  text="Save" />
              </div>
              <div className="col-auto ms-2">
                <FilePickerButton id="load-request" className="btn-outline-secondary"
                  loading={fileImporting}
                  disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                  onChange={handleImportChange}
                  text="Load"
                  accepts=".mrpreq" />
              </div>
            </div>
          </div>
          <div className="col-auto align-self-center">
            <ErrorAlert visible={pricer.error || drg.error || fileImportError || fileExportError} />
          </div>
          <div className="col-auto align-self-center">
            <div className="row g-0 justify-cotent-end">
              <div className="col-auto">
                <Button className="btn-outline-secondary" id="reset-button"
                  onClick={handleReset}
                  disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                  text="Reset" />
              </div>
              {!enableAsyncPricingApi && <div className="col-auto ms-2">
                <Button className="btn-outline-secondary" id="run-drg-grouper-button"
                  onClick={handleRunDrgGrouper}
                  disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                  loading={drg.loading}
                  text="Run DRG Grouper" />
              </div>}
              <div className="col-auto ms-2">
                <Button className="btn-primary" id="submit-button"
                  disabled={pricer.loading || fileImporting || fileExporting}
                  onClick={handleSubmit}
                  text="Submit"
                  loading={pricer.loading} />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
